import moment from "moment";
import { defineComponent, PropType } from "vue";

import { COMPONENT_NAME } from "./attributes";

import { SharedSelect } from "@/components/shared";
import { SharedSelectOption } from "@/components/shared/select/model";
import {
  ApplicationStatus,
  HistoryFiatCryptoTab,
  HistoryTab,
} from "@/shared/constants/enums";
import { HistoryFilterRequest } from "@/shared/constants/interfaces";
import { SelectOptionFactory } from "@/shared/factory/select";
import { AdminApplicationType } from "@/shared/repository/modules/admin/application/enums";
import { AdminCurrencyShowResponse } from "@/shared/repository/modules/admin/currency/repo";

type HistoryPeriod = "all" | "today" | "week" | "month";

export default defineComponent({
  name: COMPONENT_NAME,
  components: {
    SharedSelect,
  },
  props: {
    currency: {
      type: <PropType<AdminCurrencyShowResponse[]>>Array,
      default: () => [],
    },
  },
  emits: {
    apply: null,
  },
  data() {
    return {
      selectedPeriod: <HistoryPeriod>"all",
      historyRepo: this.$projectServices.historyRepo,
    };
  },
  created(): void {
    this.selectedPeriod = this.resolvePeriod();
  },
  watch: {
    "displayedFilter.date_from"(): void {
      this.selectedPeriod = this.resolvePeriod();
    },
    "displayedFilter.date_to"(): void {
      this.selectedPeriod = this.resolvePeriod();
    },
  },
  computed: {
    displayedFilter(): HistoryFilterRequest {
      return this.historyRepo.filter;
    },

    displayedTab(): HistoryTab {
      return this.historyRepo.tab;
    },

    isDepositWithdrawal(): boolean {
      return this.displayedTab === HistoryTab.depositWithdrawal;
    },

    isCrypto(): boolean {
      return (
        this.isDepositWithdrawal &&
        this.historyRepo.tabFiatCrypto === HistoryFiatCryptoTab.crypto
      );
    },

    displayedPeriodOptions(): SharedSelectOption[] {
      return [
        SelectOptionFactory({
          id: 0,
          label: this.$t("За всё время"),
          value: "all",
        }),
        SelectOptionFactory({
          id: 1,
          label: this.$t("Сегодня"),
          value: "today",
        }),
        SelectOptionFactory({ id: 2, label: this.$t("7 дней"), value: "week" }),
        SelectOptionFactory({
          id: 3,
          label: this.$t("30 дней"),
          value: "month",
        }),
      ];
    },

    displayedCurrencyOptions(): SharedSelectOption[] {
      const currencies = this.currency.filter((currency) =>
        this.isCrypto ? currency.type === "crypto" : currency.type === "fiat"
      );

      return [
        SelectOptionFactory({ id: 0, label: this.$t("Все"), value: "all" }),
        ...currencies.map((currency) =>
          SelectOptionFactory({
            id: currency.id,
            label: currency.name.toUpperCase(),
            value: this.isCrypto ? currency.name.toUpperCase() : currency.id,
            icon: currency.country,
            type: currency.type,
          })
        ),
      ];
    },

    displayedStatusOptions(): SharedSelectOption[] {
      const isApplication = this.displayedTab === HistoryTab.application;

      return [
        SelectOptionFactory({ id: 0, label: this.$t("Все"), value: "all" }),
        SelectOptionFactory({
          id: 1,
          label: this.$t("Выполнено"),
          value: ApplicationStatus.completed,
        }),
        SelectOptionFactory({
          id: 2,
          label: this.$t(isApplication ? "На проверке" : "В обработке"),
          value: isApplication
            ? ApplicationStatus.waiting
            : ApplicationStatus.inWork,
        }),
        SelectOptionFactory({
          id: 3,
          label: this.$t(
            isApplication ? "Ожидает оплаты" : "Ожидает подтверждения"
          ),
          value: ApplicationStatus.pending,
        }),
        SelectOptionFactory({
          id: 4,
          label: this.$t("Отклонено"),
          value: ApplicationStatus.reject,
        }),
      ];
    },

    displayedTypeOptions(): SharedSelectOption[] {
      return [
        SelectOptionFactory({ id: 0, label: this.$t("Все"), value: "all" }),
        SelectOptionFactory({
          id: 1,
          label: this.$t("Пополнение"),
          value: AdminApplicationType.deposit,
        }),
        SelectOptionFactory({
          id: 2,
          label: this.$t("Вывод"),
          value: AdminApplicationType.withdrawal,
        }),
      ];
    },

    displayedCurrency(): string | number {
      if (this.isCrypto) {
        return this.displayedFilter.coin || "all";
      }

      const value = this.displayedFilter.currency_id
        .replaceAll("[", "")
        .replaceAll("]", "")
        .split(",")[0];
      return value ? Number(value) : "all";
    },

    displayedStatus(): string {
      return (
        this.displayedFilter.status
          .replaceAll("[", "")
          .replaceAll("]", "")
          .split(",")[0] || "all"
      );
    },

    displayedType(): string {
      return this.displayedFilter.type || "all";
    },
  },
  methods: {
    resolvePeriod(): HistoryPeriod {
      const { date_from, date_to } = this.displayedFilter;
      if (!date_from && !date_to) {
        return "all";
      }

      const today = moment().format("DD-MM-YYYY");
      if (date_from === today && date_to === today) {
        return "today";
      }

      const sevenDaysAgo = moment().subtract(6, "days").format("DD-MM-YYYY");
      if (date_from === sevenDaysAgo && date_to === today) {
        return "week";
      }

      const thirtyDaysAgo = moment().subtract(29, "days").format("DD-MM-YYYY");
      if (date_from === thirtyDaysAgo && date_to === today) {
        return "month";
      }

      return "all";
    },

    handlePeriod(value: string | number): void {
      const period = value as HistoryPeriod;
      const today = moment();
      let date_from = "";
      let date_to = "";

      if (period === "today") {
        date_from = today.format("DD-MM-YYYY");
        date_to = date_from;
      }

      if (period === "week") {
        date_from = today.clone().subtract(6, "days").format("DD-MM-YYYY");
        date_to = today.format("DD-MM-YYYY");
      }

      if (period === "month") {
        date_from = today.clone().subtract(29, "days").format("DD-MM-YYYY");
        date_to = today.format("DD-MM-YYYY");
      }

      this.selectedPeriod = period;
      this.updateFilter({ date_from, date_to });
    },

    handleCurrency(value: string | number): void {
      const isAll = value === "all";
      this.updateFilter(
        this.isCrypto
          ? { currency_id: "", coin: isAll ? "" : String(value) }
          : { currency_id: isAll ? "" : `[${value}]`, coin: "" }
      );
    },

    handleStatus(value: string | number): void {
      this.updateFilter({ status: value === "all" ? "" : `[${value}]` });
    },

    handleType(value: string | number): void {
      this.updateFilter({ type: value === "all" ? "" : String(value) });
    },

    updateFilter(payload: Partial<HistoryFilterRequest>): void {
      this.historyRepo.CLEAR_PAGE();
      this.historyRepo.UPDATE_FILTER({
        ...this.displayedFilter,
        ...payload,
        page: 1,
      });
      this.$emit("apply");
    },
  },
});
